import { useTranslation } from 'react-i18next';

import { Input } from '@/shared/components/ui/Input/Input';
import { cn } from '@/shared/utils/cn/cn.util';

import {
  KEY_VALUE_CELL_CLASSES,
  KEY_VALUE_CELL_LABEL_CLASSES,
  KEY_VALUE_KEY_DESCRIPTION_CLASSES,
  KEY_VALUE_KEY_INPUT_CLASSES,
  KEY_VALUE_KEY_LABEL_CLASSES,
} from '../structured.tokens';

import { type KeyValueKeyCellProps } from './KeyValueKeyCell.types';

/**
 * A row's key — **chosen** when the caller has a catalogue, typed when it does
 * not.
 *
 * A catalogue key renders as its label and description, with no text box at all.
 * That is the point of the catalogue rather than a side effect of it: U0 says a
 * known set of values never gets a free-text control, and the invalid state
 * disappears instead of being warned about. `media.maxUploadBytes` is an
 * exhaustive `z.record(z.enum(MEDIA_KINDS), …)` — a typo'd key there was a 422
 * the operator could neither see coming nor act on.
 *
 * Presentation only (U5).
 */
export function KeyValueKeyCell({
  row,
  index,
  size,
  placeholder,
  isDisabled,
  isReadOnly,
  onKeyChange,
  onBlur,
}: KeyValueKeyCellProps) {
  const { t } = useTranslation('uiKit');

  if (row.entry !== null) {
    return (
      <div className={KEY_VALUE_CELL_CLASSES}>
        <span className={KEY_VALUE_KEY_LABEL_CLASSES}>
          {row.entry.label}
          {row.entry.description === undefined ? null : (
            <span className={KEY_VALUE_KEY_DESCRIPTION_CLASSES}>{row.entry.description}</span>
          )}
        </span>
      </div>
    );
  }

  return (
    <div className={KEY_VALUE_CELL_CLASSES}>
      <span aria-hidden className={KEY_VALUE_CELL_LABEL_CLASSES}>
        {t('keyValue.keyLabel')}
      </span>
      <Input
        aria-label={
          row.key.trim() === ''
            ? t('keyValue.keyFor', { index: index + 1 })
            : t('keyValue.keyForNamed', { key: row.key })
        }
        value={row.key}
        placeholder={placeholder ?? t('keyValue.keyLabel')}
        disabled={isDisabled}
        readOnly={isReadOnly}
        isInvalid={row.isDuplicate}
        inputSize={size}
        spellCheck={false}
        autoComplete="off"
        // A map key is an identifier, not prose: Latin and left-to-right
        // whatever the interface language is.
        dir="ltr"
        className={cn(KEY_VALUE_KEY_INPUT_CLASSES)}
        onBlur={onBlur}
        onChange={(event) => {
          onKeyChange(event.target.value);
        }}
      />
    </div>
  );
}
